import { Component, EventEmitter, Injector, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, ValidatorFn } from '@angular/forms';
import { Router } from '@angular/router';
import { AlertService } from '@app/@core/services/alert.service';
import { HttpService } from '@app/@core/services/http.service';
import { LocalService } from '@app/@core/services/local.service';
import { DataGridControlService } from '@app/shared/modules/app-grid-youth-list/data-grid-control.service';
import { DynamicsearchService } from '@pics-core/dynamic';
import moment from 'moment';
import { Tooltip } from 'primeng/tooltip';
import { YouthQuickSearchService } from './@core/youth-quick-search.service';

export function specialCharacterValidator(): ValidatorFn {
  return (control: AbstractControl): { [key: string]: any } | null => {
    /* This regex disallows special characters */
    const forbidden = /[^a-zA-Z ]/g.test(control.value);
    return forbidden ? { specialCharacter: { value: control.value } } : null;
  };
}
export function specialNumberValidator(): ValidatorFn {
  return (control: AbstractControl): { [key: string]: any } | null => {
    /* This regex disallows number */
    const forbidden = /[^0-9 ]/g.test(control.value);
    return forbidden ? { specialNumberValidator: { value: control.value } } : null;
  };
}

@Component({
  standalone: false,
  selector: 'app-youth-quick-search',
  templateUrl: './youth-quick-search.component.html',
  styleUrls: ['./youth-quick-search.component.scss']
})
export class YouthQuickSearchComponent implements OnInit, OnDestroy {
  @Output() youthSearchData = new EventEmitter<any[]>();
  @ViewChild(Tooltip) tooltip!: Tooltip;
  youthQuickSearchForm: FormGroup;
  sexOptions: any;
  updateGrid: any;
  youthSearchResults: any;
  tableColumns: any;
  totalCount: any;
  dateType: any;
  dateTimeType: any;
  youthNoteBookDetails: any;
  formHasValues: boolean = false;
  dynamicSearchService: any;
  HttpService: any;
  pageId: string;
  localstorage: LocalService;
  gridId: string;
  toolTipToggle: boolean = false;
  constructor(
    private formBuilder: FormBuilder,
    injector: Injector,
    private youthSearchService: YouthQuickSearchService,
    private router: Router,
    private alertService: AlertService,
    private dataGridControlService: DataGridControlService
  ) {
    this.localstorage = injector.get<LocalService>(LocalService);
    this.youthQuickSearchForm = this.formBuilder.group({
      jjisNumber: ['', [specialNumberValidator()]],
      firstName: ['', [specialCharacterValidator()]],
      lastName: ['', [specialCharacterValidator()]],
      dateOfBirth: [''],
      age: ['', [specialNumberValidator()]],
      sex: [''],
      soundexSearch: ['']
    });
    this.dateType = 'date';
    this.dateTimeType = 'datetime';
    const parentGridPage = {
      id: null,
      name: 'youthsearch'
    };
    this.localstorage.setItem('backToGridPage', JSON.stringify(parentGridPage));
    this.dynamicSearchService = injector.get<DynamicsearchService>(DynamicsearchService);
    this.HttpService = injector.get<HttpService>(HttpService);
    this.dynamicSearchService.SetHttpService(this.HttpService);
    this.gridId = 'youth-search-grid';
  }
  ngOnInit(): void {
    this.setGridColumns();
    this.getGenderValues();
    this.getYouthNoteBookPageId();
  }
  ngOnDestroy(): void {
    this.youthSearchReset();
  }

  showTooltip() {
    this.tooltip.activate();
  }

  hideTooltip() {
    this.tooltip.deactivate();
  }

  toggleTooltip() {
    if (!this.toolTipToggle) {
      this.tooltip.activate();
    } else {
      this.tooltip.deactivate();
    }
    this.toolTipToggle = !this.toolTipToggle;
  }

  validateNumericInputKeydown(event: KeyboardEvent, inputLength) {
    const allowedKeys = ['Backspace', 'Delete', 'Tab', 'Escape', 'Enter', 'Home', 'End', 'ArrowLeft', 'ArrowRight'];

    if (allowedKeys.includes(event.key)) {
      return;
    }

    // Allow: Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
    if (['a', 'c', 'v', 'x'].includes(event.key.toLowerCase()) && (event.ctrlKey || event.metaKey)) {
      return;
    }

    // Ensure that it is a number and stop the keypress
    if (!/^[0-9]$/.test(event.key)) {
      event.preventDefault();
    }

    // Optional: Prevent input if it would result in more than 3 digits
    const input = event.target as HTMLInputElement;
    if (input.value.length >= inputLength && input.selectionStart === input.selectionEnd) {
      event.preventDefault();
    }
  }

  setGridColumns() {
    const columns = [
      {
        columnDef: 'jjis_number',
        header: 'JJIS #',
        cell: (element: any) => `${element.jjis_number}`,
        dateFormat: false,
        icon: false,
        filter: true,
        link: true,
        // linkname: { name: 'routeTo', routeLink: `pages/setting/data-source`},
        sort: false,
        hide: false,
        fixed: true,
        Choosable: false,
        visible: true,
        allowHiding: false,
        selected: ['filter', 'link']
      },
      {
        columnDef: 'full_name',
        header: 'Youth Name',
        cell: (element: any) => `${element.full_name}`,
        dateFormat: false,
        icon: false,
        filter: true,
        link: false,
        sort: false,
        hide: false,
        fixed: true,
        Choosable: false,
        visible: true,
        allowHiding: false,
        selected: ['filter']
      },
      {
        columnDef: 'dob',
        header: 'DOB',
        cell: (element: any) => `${element.dob}`,
        dateFormat: false,
        icon: false,
        filter: true,
        link: false,
        sort: false,
        hide: false,
        fixed: false,
        Choosable: false,
        visible: true,
        allowHiding: true,
        selected: ['filter']
      },
      {
        columnDef: 'age',
        header: 'Age',
        cell: (element: any) => `${element.age}`,
        dateFormat: false,
        icon: false,
        filter: true,
        link: false,
        sort: false,
        hide: false,
        fixed: false,
        Choosable: false,
        visible: true,
        allowHiding: true,
        selected: ['filter']
      },
      {
        columnDef: 'gender',
        header: 'Sex',
        cell: (element: any) => `${element.gender}`,
        dateFormat: false,
        icon: false,
        filter: true,
        link: false,
        sort: false,
        hide: false,
        fixed: false,
        Choosable: false,
        visible: true,
        allowHiding: true,
        selected: ['filter']
      },
      {
        columnDef: 'county',
        header: 'County',
        cell: (element: any) => `${element.county}`,
        dateFormat: false,
        icon: false,
        filter: true,
        link: false,
        sort: false,
        hide: false,
        fixed: false,
        Choosable: false,
        visible: true,
        allowHiding: true,
        selected: ['filter']
      },
      {
        columnDef: 'primary_worker',
        header: 'Primary Worker',
        cell: (element: any) => `${element.primary_worker}`,
        dateFormat: false,
        icon: false,
        filter: true,
        link: false,
        sort: false,
        hide: false,
        fixed: false,
        Choosable: false,
        visible: true,
        allowHiding: true,
        selected: ['filter']
      },
      {
        columnDef: 'opened',
        header: 'Opened',
        cell: (element: any) => `${element.opened}`,
        dateFormat: this.dateType,
        icon: false,
        filter: true,
        link: false,
        sort: false,
        hide: false,
        fixed: false,
        Choosable: false,
        visible: true,
        allowHiding: true,
        selected: ['filter']
      },
      {
        columnDef: 'closed',
        header: 'Closed',
        cell: (element: any) => `${element.closed}`,
        dateFormat: this.dateType,
        icon: false,
        filter: true,
        link: false,
        sort: false,
        hide: false,
        fixed: false,
        Choosable: false,
        visible: true,
        allowHiding: true,
        selected: ['filter']
      },
      {
        columnDef: 'disposition_category',
        header: 'Disposition Category',
        cell: (element: any) => `${element.disposition_category}`,
        dateFormat: false,
        icon: false,
        filter: true,
        link: false,
        sort: false,
        hide: false,
        fixed: false,
        Choosable: false,
        visible: false,
        allowHiding: true,
        selected: ['filter']
      },
      {
        columnDef: 'disposition_description',
        header: 'Disposition Description',
        cell: (element: any) => `${element.disposition_description}`,
        dateFormat: false,
        icon: false,
        filter: true,
        link: false,
        sort: false,
        hide: false,
        fixed: false,
        Choosable: false,
        visible: false,
        allowHiding: true,
        selected: ['filter']
      },
      {
        columnDef: 'disposition_ordered_date',
        header: 'Ordered Date',
        cell: (element: any) => `${element.disposition_ordered_date}`,
        dateFormat: this.dateType,
        icon: false,
        filter: true,
        link: false,
        sort: false,
        hide: false,
        fixed: false,
        Choosable: false,
        visible: false,
        allowHiding: true,
        selected: ['filter']
      },
      {
        columnDef: 'current_location',
        header: 'Current Location',
        cell: (element: any) => `${element.current_location}`,
        dateFormat: false,
        icon: false,
        filter: true,
        link: false,
        sort: false,
        hide: false,
        fixed: false,
        Choosable: false,
        visible: false,
        allowHiding: true,
        selected: ['filter']
      },
      {
        columnDef: 'actual_location',
        header: 'Temporary Location',
        cell: (element: any) => `${element.actual_location}`,
        dateFormat: false,
        icon: false,
        filter: true,
        link: false,
        sort: false,
        hide: false,
        fixed: false,
        Choosable: false,
        visible: false,
        allowHiding: true,
        selected: ['filter']
      }
    ];
    this.dynamicSearchService.getStaticGridConfig(this.gridId).subscribe((res: any) => {
      if (res.data && res.data?.config) {
        const configArray = JSON.parse(res.data.config);
        const userPreferenceGridConfig = this.applyUserPreference(columns, configArray);
        this.tableColumns = userPreferenceGridConfig;
      } else {
        this.tableColumns = columns;
      }
      this.updateGrid = {
        editRecord: false,
        viewRecord: false,
        lock: false,
        version: false,
        duplicate: false,
        delete: false,
        externalLink: false,
        linkVisible: false,
        chooser: true
      };
    });
  }
  applyUserPreference(gridConfig, userPreferenceConfig) {
    const options = [
      'width',
      'filterValues',
      'sortOrder',
      'sortIndex',
      'visible',
      'visibleIndex',
      'filterValue',
      'filterType',
      'selectedFilterOperation'
    ];
    for (const column of gridConfig) {
      const columnPreferences = userPreferenceConfig.find(
        preferenceColumn => preferenceColumn.columnDef === column.columnDef
      );
      if (columnPreferences) {
        for (const option of options) {
          if (option === 'visibleIndex' && columnPreferences.hide) {
            delete column[option];
          } else if (Object.prototype.hasOwnProperty.call(columnPreferences, option)) {
            column[option] = columnPreferences[option];
          }
        }
      }
    }
    return this.assignVisibleIndexes(gridConfig);
  }

  assignVisibleIndexes(gridConfig) {
    const visibleColumns =
      (gridConfig?.length && gridConfig?.filter(col => !col.hide)?.sort((a, b) => a.visibleIndex - b.visibleIndex)) ||
      [];
    const nonVisibleColumns =
      (gridConfig?.length &&
        gridConfig
          ?.filter(col => col.hide || col?.visibleIndex < 0)
          ?.sort((a, b) => a.visibleIndex - b.visibleIndex)) ||
      [];
    if (visibleColumns && visibleColumns.length) {
      for (const [index, column] of visibleColumns.entries()) {
        column.visibleIndex = index;
      }
    }
    if (nonVisibleColumns && nonVisibleColumns.length) {
      for (const [index, column] of nonVisibleColumns.entries()) {
        column.visibleIndex = visibleColumns?.length + (index + 1);
      }
    }
    return [...visibleColumns, ...nonVisibleColumns];
  }

  trimSpaces(element) {
    const getElement = this.youthQuickSearchForm.controls[`${element}`];
    const getValue = getElement.value.trim();
    getElement.setValue(getValue);
  }

  checkFormValues() {
    const getValues = this.youthQuickSearchForm.getRawValue();
    const jjisNumberControl = this.youthQuickSearchForm.get('jjisNumber');
    const otherControls = this.getOtherControls('jjisNumber');

    this.handleJJISField(jjisNumberControl, otherControls);
    this.handleOtherFields(jjisNumberControl, otherControls);

    if (this.isInvalidSoundexSearch(getValues)) return;
    if (this.isInvalidSexWithNameOrDob(getValues)) return;
    if (this.isInvalidJJISNumber(jjisNumberControl)) return;
    if (this.isInvalidNameField('firstName')) return;
    if (this.isInvalidNameField('lastName')) return;
    if (this.isInvalidAgeField(getValues)) return;
    if (this.isInvalidFirstNameWithOtherFields(getValues)) return;

    this.formHasValues = this.checkFormHasValues();
  }

  private getOtherControls(excludeControlName: string) {
    return Object.keys(this.youthQuickSearchForm.controls)
      .filter(control => control !== excludeControlName)
      .map(control => this.youthQuickSearchForm.get(control));
  }

  private handleJJISField(jjisNumberControl, otherControls) {
    jjisNumberControl.valueChanges.subscribe(value => {
      if (value) {
        otherControls.forEach(control => control.reset());
      }
    });
  }

  private handleOtherFields(jjisNumberControl, otherControls) {
    otherControls.forEach(control => {
      control.valueChanges.subscribe(value => {
        if (value) {
          jjisNumberControl.reset();
        }
      });
    });
  }

  private isInvalidSoundexSearch(getValues) {
    if (getValues.soundexSearch && getValues?.lastName?.length < 1) {
      this.formHasValues = false;
      return true;
    }
    return false;
  }

  private isInvalidSexWithNameOrDob(getValues) {
    const isNameOrDobEmpty = !getValues.lastName && !getValues.firstName && !getValues.dateOfBirth;
    if (getValues.sex && isNameOrDobEmpty) {
      this.formHasValues = false;
      return true;
    }
    return false;
  }

  private isInvalidJJISNumber(jjisNumberControl) {
    if (jjisNumberControl?.value && jjisNumberControl?.value !== '' && jjisNumberControl?.invalid) {
      this.formHasValues = false;
      return true;
    }
    return false;
  }

  private isInvalidNameField(fieldName: string) {
    const control = this.youthQuickSearchForm.get(fieldName);
    if (control?.invalid) {
      this.formHasValues = false;
      return true;
    }
    return false;
  }

  private isInvalidAgeField(getValues) {
    const ageControl = this.youthQuickSearchForm.get('age');
    const isNameOrDobEmpty = !getValues.lastName && !getValues.firstName && !getValues.dateOfBirth;
    if (
      (ageControl?.value && ageControl?.value !== '' && ageControl?.invalid) ||
      (ageControl?.valid && getValues.age && isNameOrDobEmpty)
    ) {
      this.formHasValues = false;
      return true;
    }
    return false;
  }

  private isInvalidFirstNameWithOtherFields(getValues) {
    if (
      (!getValues.lastName || getValues.lastName === '') &&
      (!getValues.dateOfBirth || getValues.dateOfBirth === '') &&
      (!getValues.sex || getValues.sex === '') &&
      (!getValues.age || getValues.age === '') &&
      getValues.firstName !== '' &&
      getValues?.firstName?.length < 2
    ) {
      this.formHasValues = false;
      return true;
    }
    return false;
  }

  private checkFormHasValues() {
    return Object.values(this.youthQuickSearchForm.controls).some(control => control.value);
  }

  youthSearchReset() {
    this.youthQuickSearchForm.controls['jjisNumber'].patchValue('');
    this.youthQuickSearchForm.controls['firstName'].patchValue('');
    this.youthQuickSearchForm.controls['lastName'].patchValue('');
    this.youthQuickSearchForm.controls['dateOfBirth'].patchValue('');
    this.youthQuickSearchForm.controls['age'].patchValue('');
    this.youthQuickSearchForm.controls['sex'].patchValue('');
    this.youthQuickSearchForm.controls['soundexSearch'].patchValue('');
    this.youthSearchResults = [];
    this.clearGrid();
    this.checkFormValues();
    this.setGridColumns();
  }
  youthSearchSubmit() {
    this.clearGrid();
    this.setGridColumns();
    this.youthSearchResults = [];
    const jjisNumber = this.youthQuickSearchForm.controls['jjisNumber'].value;
    const firstName = this.youthQuickSearchForm.controls['firstName'].value;
    const lastName = this.youthQuickSearchForm.controls['lastName'].value;
    const dateOfBirth = this.youthQuickSearchForm.controls['dateOfBirth'].value;
    let age = this.youthQuickSearchForm.controls['age'].value;
    const sex = this.youthQuickSearchForm.controls['sex'].value;
    const soundexSearch = this.youthQuickSearchForm.controls['soundexSearch'].value;
    let formattedDateOfBirth = '';
    if (dateOfBirth) {
      formattedDateOfBirth = moment(dateOfBirth).format('YYYY-MM-DD');
      age = '';
    }
    const obj = {
      lastName: lastName,
      firstName: firstName,
      dob: formattedDateOfBirth,
      jjisNumber: jjisNumber,
      age: age,
      gender_code: sex,
      soundexSearch: soundexSearch
    };
    this.youthSearchService.searchYouth(obj).subscribe(
      (res: any) => {
        if (res && res.data && res.data.length) {
          res.data.map(r => {
            r.gender = this.mapGender(r.gender);
            r.opened = this.formatDate(r.opened);
            r.closed = this.formatDate(r.closed);
            r.disposition_ordered_date = this.formatDate(r.disposition_ordered_date);
          });

          this.youthSearchResults = res.data;
          this.totalCount = res.data.length;
        }
      },
      (err: any) => this.alertService.error(err.error.message)
    );
  }

  private mapGender(gender) {
    // Return as is if no gender is provided
    if (!gender) return gender;

    const genderMap = {
      m: 'Male',
      f: 'Female',
      u: 'Unknown',
      e: 'Either',
      b: 'Both',
      x: 'Non-Binary'
    };
    // Default to original if not found
    return genderMap[gender.toLowerCase()] || gender;
  }

  private formatDate(dateString) {
    return dateString && dateString.includes('T') ? dateString.split('T')[0] : dateString;
  }

  getYouthNoteBookPageId() {
    this.youthSearchService
      .getYouthNoteBookActiveVersionId()
      .subscribe((res: any) => (this.youthNoteBookDetails = res.data));
  }

  routeTo(evt) {
    this.router.navigateByUrl(
      `/pages/dynamic-routing/tab/${this.youthNoteBookDetails?.activeVersion?.id}/${evt.data.entity_id}/youth.youth_id/${evt.data.youth_id}/number`
    );
  }

  getGenderValues() {
    this.youthSearchService.getGenderValues().subscribe((res: any) => {
      const sexOptions = res?.data.map(item => ({
        name: item?.description,
        value: item?.code
      }));
      this.sexOptions = sexOptions;
    });
  }
  clearGrid() {
    this.dataGridControlService.clearSortingAndFiltering();
  }

  handleManualEntry(event: any) {
    const value = event.target.value;
    const getValues = this.youthQuickSearchForm.getRawValue();
    const parsedDate = this.parseDate(value);
    if (parsedDate) {
      getValues.dateOfBirth = parsedDate;
      this.checkFormValues();
    }
  }

  handleDateInputKeydown(event: KeyboardEvent) {
    const allowedKeys = [
      'Backspace',
      'Delete',
      'Tab',
      'Escape',
      'Enter',
      'ArrowLeft',
      'ArrowRight',
      'ArrowUp',
      'ArrowDown',
      'Home',
      'End'
    ];

    if (this.isAllowedKey(event, allowedKeys)) {
      this.handleAllowedKeys(event);
      return;
    }

    const input = event.target as HTMLInputElement;
    const selectionStart = input.selectionStart || 0;

    if (this.isLengthExceeded(input.value.length, selectionStart)) {
      event.preventDefault();
      return;
    }

    if (this.isSlashAllowed(event, selectionStart)) {
      return;
    }

    if (this.isInvalidInput(event)) {
      event.preventDefault();
      return;
    }

    if (this.isInvalidMonth(event, input, selectionStart)) {
      event.preventDefault();
      return;
    }

    if (this.isInvalidDay(event, input, selectionStart)) {
      event.preventDefault();
      return;
    }

    this.insertSlashIfNeeded(input, selectionStart, event);
  }

  private isAllowedKey(event: KeyboardEvent, allowedKeys: string[]): boolean {
    return allowedKeys.includes(event.key);
  }

  private handleAllowedKeys(event: KeyboardEvent) {
    const input = event.target as HTMLInputElement;
    if ((event.key === 'Backspace' || event.key === 'Delete') && input.value.length <= 10) {
      this.youthQuickSearchForm.patchValue({ dateOfBirth: null });
      this.checkFormValues();
    }
  }

  private isLengthExceeded(valueLength: number, selectionStart: number): boolean {
    return valueLength >= 10 && selectionStart === valueLength;
  }

  private isSlashAllowed(event: KeyboardEvent, selectionStart: number): boolean {
    return event.key === '/' && (selectionStart === 2 || selectionStart === 5);
  }

  private isInvalidInput(event: KeyboardEvent): boolean {
    return !/^[0-9]$/.test(event.key);
  }

  private isInvalidMonth(event: KeyboardEvent, input: HTMLInputElement, selectionStart: number): boolean {
    if (selectionStart === 0) {
      return !['0', '1'].includes(event.key);
    } else if (selectionStart === 1) {
      const firstChar = input.value[0];
      return (firstChar === '0' && event.key === '0') || (firstChar === '1' && !['0', '1', '2'].includes(event.key));
    }
    return false;
  }

  private isInvalidDay(event: KeyboardEvent, input: HTMLInputElement, selectionStart: number): boolean {
    if (selectionStart === 3) {
      const month = input.value.slice(0, 2);
      return (
        (month === '02' && !['0', '1', '2'].includes(event.key)) ||
        (month !== '02' && !['0', '1', '2', '3'].includes(event.key))
      );
    } else if (selectionStart === 4) {
      const month = input.value.slice(0, 2);
      const dayFirstDigit = input.value[3];
      return (
        (['04', '06', '09', '11'].includes(month) && dayFirstDigit === '3' && event.key !== '0') ||
        (dayFirstDigit === '3' && !['0', '1'].includes(event.key)) ||
        (dayFirstDigit === '0' && event.key === '0')
      );
    }
    return false;
  }

  private insertSlashIfNeeded(input: HTMLInputElement, selectionStart: number, event: KeyboardEvent) {
    if ((selectionStart === 2 || selectionStart === 5) && input.value[selectionStart] !== '/') {
      const newValue = input.value.slice(0, selectionStart) + '/' + input.value.slice(selectionStart);
      input.value = newValue;
      this.youthQuickSearchForm.patchValue({ dateOfBirth: newValue });
      input.setSelectionRange(selectionStart + 1, selectionStart + 1);
      event.preventDefault();
    }
  }

  parseDate(value: string): Date | null {
    const parts = value.split('/');
    if (parts.length === 3) {
      const month = parseInt(parts[0], 10) - 1;
      const day = parseInt(parts[1], 10);
      const year = parseInt(parts[2], 10);
      if (!isNaN(month) && !isNaN(day) && !isNaN(year)) {
        return new Date(year, month, day);
      }
    }
    return null;
  }
}
